import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Button,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  Paper,
  Pagination,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CloudUpload as UploadIcon,
  Assessment as ValidateIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  DeleteSweep as BulkDeleteIcon,
  Assignment as AssignIcon,
  Add as AddIcon,
  Edit as EditIcon,
  RateReview as ReviewIcon,
  CalendarToday as CalendarIcon,
  WorkOutline,
  EventNote,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { jobDescriptionService } from '../services/jobDescriptionService';
import { resumeService } from '../services/resumeService';
import SnackbarAlert from '../components/SnackbarAlert';
import ResumeViewUpdateDialog from '../components/ResumeViewUpdateDialog';
import InterviewSchedulingModal from '../components/InterviewSchedulingModal';
import ResumeValidationDialog from '../components/ResumeValidationDialog';
import UploadResumeDialog from '../components/molecules/UploadResumeDialog';
import './ResumesPage.css';
import { COLORS } from '../config/colors';
import { API_ENDPOINTS } from '../config/apiEndpoints';

interface Resume {
  id: string;
  candidateName: string;
  candidateEmail?: string;
  email?: string;
  phone?: string;
  jobId: string;
  jobTitle?: string;
  status: string;
  score?: number;
  uploadedDate?: string;
  createdAt?: string;
  assignedPanel?: string;
  filePath?: string;
  fileName?: string;
  resume_location?: string;
  _original?: any;
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface JobDescription {
  id: string;
  jobId?: string;
  title: string;
  jobTitle?: string;
  status: string;
  event?: boolean;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

// Resume status options with colors
const RESUME_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted', color: '#3b82f6' },
  { value: 'reviewing', label: 'Reviewing', color: '#f59e0b' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: '#8b5cf6' },
  { value: 'rejected', label: 'Rejected', color: '#ef4444' },
  { value: 'hired', label: 'Hired', color: '#10b981' },
  { value: 'withdrawn', label: 'Withdrawn', color: '#6b7280' },
  { value: 'shortlisted', label: 'Shortlisted', color: '#06b6d4' },
  { value: 'interviewed', label: 'Interviewed', color: '#84cc16' },
  { value: 'move_to_next_panel', label: 'Move to Next Panel', color: '#f97316' },
];

const ResumesPage: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const userRole = user?.role || 'sme';

  // State management - ADD score filter state
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [jobs, setJobs] = useState<JobDescription[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedResumes, setSelectedResumes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterJobId, setFilterJobId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterScore, setFilterScore] = useState(''); // ADD score filter state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  //  : Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResumes, setTotalResumes] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingResumes, setIsLoadingResumes] = useState(false);

  // Add state for the interview scheduling modal
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [selectedResumeForInterview, setSelectedResumeForInterview] = useState<Resume | null>(null);

  // Add state for validation dialog
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalResumes: 0,
    underReview: 0,
    selected: 0,
    rejected: 0,
    shortlisted: 0,
  });

  // Tab-specific counts
  const [tabCounts, setTabCounts] = useState({
    jd: 0,
    event: 0,
  });

  //  : Pagination handler functions
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      const type = currentTab === 0 ? 'jd' : 'event';
      fetchResumes(newPage, pageSize, type);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setCurrentPage(1);
    setSearchTerm('');
    setFilterJobId('');
    setFilterStatus('');
    setFilterScore('');
    
    // Determine type based on tab selection and make fresh API call
    const type = newValue === 0 ? 'jd' : 'event';
    const otherType = newValue === 0 ? 'event' : 'jd';
    console.log('Tab changed to:', newValue === 0 ? 'Job Descriptions' : 'Events', 'with type:', type);
    
    // Make fresh API call with the new type
    fetchResumes(1, pageSize, type); // Reset to first page with new type
    
    // Also refresh the count for the other tab to keep headers updated
    fetchResumeCount(otherType);
  };

  // Add these functions after your handleTabChange function (around line 130-140):

  // Apply search and filter criteria (no tab filtering needed since API filters by type)
  const getFilteredResumes = () => {
    console.log('Filtering resumes, total count:', resumes.length);
    console.log('Current tab:', currentTab, 'Type:', currentTab === 0 ? 'jd' : 'event');
    
    return resumes.filter(resume => {
      const matchesSearch =
        !searchTerm ||
        resume.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (resume.email && resume.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        resume.jobId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (resume.jobTitle && resume.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesJobFilter =
        !filterJobId ||
        resume.jobId === filterJobId ||
        resume.jobId.toLowerCase().includes(filterJobId.toLowerCase()) ||
        (resume.jobTitle && resume.jobTitle.toLowerCase().includes(filterJobId.toLowerCase()));

      const matchesStatusFilter = !filterStatus || resume.status === filterStatus;

      const matchesScoreFilter =
        !filterScore ||
        (() => {
          const score = resume.score;

          if (filterScore === 'not_validated') {
            return !score || score === 0;
          }

          if (!score || score === 0) {
            return false;
          }

          switch (filterScore) {
            case '90+':
              return score >= 90;
            case '80-89':
              return score >= 80 && score < 90;
            case '70-79':
              return score >= 70 && score < 80;
            case '60-69':
              return score >= 60 && score < 70;
            case '50-59':
              return score >= 50 && score < 60;
            case '<50':
              return score < 50;
            default:
              return true;
          }
        })();

      return matchesSearch && matchesJobFilter && matchesStatusFilter && matchesScoreFilter;
    });
  };

  // Update job filter options to be tab-specific
  const getJobFilterOptions = () => {
    // Filter jobs based on current tab
    const tabSpecificJobs = jobs.filter(job => {
      if (currentTab === 0) {
        return !job.event; // JD Tab - show non-event jobs
      } else {
        return job.event === true; // Event Tab - show event jobs
      }
    });

    // Also include jobs from current resumes that match the tab
    const jobsFromResumes = resumes
      .filter(resume => {
        const correspondingJob = jobs.find(job => job.id === resume.jobId);
        if (currentTab === 0) {
          return !correspondingJob?.event;
        } else {
          return correspondingJob?.event === true;
        }
      })
      .map(resume => ({
        id: resume.jobId,
        title: resume.jobTitle || 'Unknown Job',
        event: false, // Default value
      }));

    const allJobs = [...tabSpecificJobs, ...jobsFromResumes];
    const uniqueJobs = allJobs.reduce((acc: any[], job) => {
      if (!acc.find(existingJob => existingJob.id === job.id)) {
        acc.push(job);
      }
      return acc;
    }, []);

    uniqueJobs.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
    return uniqueJobs;
  };

  //  Fetch resumes with pagination
  const fetchResumes = async (page: number = currentPage, size: number = pageSize, type?: string) => {
    if (isLoadingResumes) return;
    setIsLoadingResumes(true);

    try {
      let resumeData;

      console.log('User role:', userRole);
      console.log('User object:', user);
      console.log('Fetching resumes with type:', type);

      if (userRole === 'hr' || userRole === 'admin') {
        // For HR/Admin, don't send emailID - they get all resumes
        console.log('HR/Admin user - fetching all resumes without emailID');
        resumeData = await resumeService.getAllResumes({
          page,
          size,
          role: userRole,
          type,
        });
      } else {
        // For SME users, send emailID
        const userEmail = user?.email;

        console.log('SME User email for API call:', userEmail);

        if (!userEmail) {
          console.error('No email found in user object:', user);
          setSnackbar({
            open: true,
            message: 'User email not found. Please contact administrator or try logging in again.',
            severity: 'error',
          });
          setResumes([]);
          setIsLoadingResumes(false);
          return;
        }

        resumeData = await resumeService.getAllResumes({
          emailID: userEmail,
          page,
          size,
          role: userRole,
          type,
        });
      }

      console.log('Fetched resume data:', resumeData);
      console.log('API Response type parameter:', type);
      console.log('Current tab when fetching:', currentTab);

      // Handle different response formats
      let resumeList: any[] = [];
      if (resumeData.items && Array.isArray(resumeData.items)) {
        resumeList = resumeData.items;
      } else if (resumeData.resumes && Array.isArray(resumeData.resumes)) {
        resumeList = resumeData.resumes;
      } else if (resumeData.data && Array.isArray(resumeData.data)) {
        resumeList = resumeData.data;
      } else if (Array.isArray(resumeData)) {
        resumeList = resumeData;
      } else {
        console.warn('Unexpected resume data format:', resumeData);
        resumeList = [];
      }

      console.log('Resume list extracted:', resumeList);

      const normalizedResumes = resumeList.map((resume: any) => {
        const normalized = {
          id: resume.resume_id || resume.id || resume.resumeId,
          candidateName:
            resume.candidate_name || resume.candidateName || resume.name || 'Unknown Candidate',
          email: resume.candidate_email || resume.candidateEmail || resume.email || '',
          phone: resume.phone || resume.phoneNumber || '',
          jobId: resume.job_id || resume.jobId || '',
          jobTitle: resume.job_title || resume.jobTitle || 'Unknown Job',
          status: resume.resume_status || resume.status || 'N/A',
          score: resume.score || null,
          uploadedDate:
            resume.createdAt ||
            resume.created_at ||
            resume.uploadedDate ||
            resume.uploaded_date ||
            new Date().toISOString(),
          assignedPanel:
            resume.assigned_sme ||
            resume.assignedSME ||
            (resume.panel_list && resume.panel_list.length > 0 ? resume.panel_list[0] : '') ||
            '',
          filePath: resume.file_path || resume.filePath || '',
          fileName: resume.file_name || resume.fileName || '',
          resume_location: resume.resume_location || resume.file_path || resume.filePath || '',
          _original: resume,
        };

        // Debug logging for panel assignments
        if (resume.panel_list && resume.panel_list.length > 0) {
          console.log(`Resume ${normalized.candidateName} has panel_list:`, resume.panel_list);
          console.log(`Mapped to assignedPanel:`, normalized.assignedPanel);
        }

        return normalized;
      });

      console.log('Normalized resumes:', normalizedResumes);
      console.log('Setting resumes with type:', type, 'Count:', normalizedResumes.length);
      setResumes(normalizedResumes);

      //  : Update pagination data
      setTotalResumes(resumeData.total || resumeList.length);
      setTotalPages(resumeData.pages || Math.ceil((resumeData.total || resumeList.length) / size));
      setCurrentPage(resumeData.page || page);

      // Update tab-specific count based on current type
      if (type) {
        setTabCounts(prev => ({
          ...prev,
          [type]: resumeData.total || resumeList.length
        }));
        console.log(`Updated ${type} tab count to:`, resumeData.total || resumeList.length);
      }

      //   Calculate stats based on actual resume_status values from API
      const totalCount = resumeData.total || normalizedResumes.length;

      // Get unique statuses from the actual data
      const statusCounts = normalizedResumes.reduce((acc: any, resume: Resume) => {
        const status = resume.status || 'N/A';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      console.log('Status counts from API data:', statusCounts);

      setStats({
        totalResumes: totalCount,
        underReview: statusCounts['reviewing'] || statusCounts['under_review'] || 0,
        selected:
          statusCounts['selected'] || statusCounts['hired'] || statusCounts['shortlisted'] || 0,
        rejected: statusCounts['rejected'] || 0,
        shortlisted: statusCounts['shortlisted'] || 0,
      });
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch resumes. Please try again.',
        severity: 'error',
      });
      setResumes([]);
      setTotalResumes(0);
      setTotalPages(0);
      setStats({
        totalResumes: 0,
        underReview: 0,
        selected: 0,
        rejected: 0,
        shortlisted: 0,
      });
    } finally {
      setIsLoadingResumes(false);
    }
  };

  // Helper function to fetch count for a specific type without updating resumes
  const fetchResumeCount = async (type: string) => {
    try {
      let resumeData;
      
      console.log(`Fetching count for type: ${type}`);

      if (userRole === 'hr' || userRole === 'admin') {
        resumeData = await resumeService.getAllResumes({
          page: 1,
          size: 1, // Just need count, not actual data
          role: userRole,
          type,
        });
      } else {
        const userEmail = user?.email;
        if (!userEmail) {
          console.error('No email found for count fetch');
          return;
        }

        resumeData = await resumeService.getAllResumes({
          emailID: userEmail,
          page: 1,
          size: 1, // Just need count, not actual data
          role: userRole,
          type,
        });
      }

      // Update only the count for this type
      setTabCounts(prev => ({
        ...prev,
        [type]: resumeData.total || 0
      }));
      
      console.log(`Updated ${type} tab count to:`, resumeData.total || 0);
    } catch (error) {
      console.error(`Error fetching count for ${type}:`, error);
    }
  };

  //  : Fetch data on component mount - Use pagination
  useEffect(() => {
    if (!authLoading && user) {
      // Fetch initial data for JD tab (default tab)
      fetchResumes(1, pageSize, 'jd');
      
      // Also fetch count for Events tab to show correct tab header
      fetchResumeCount('event');
      
      fetchJobs();
      loadInterviewPanels();
    }
  }, [userRole, authLoading, user]);

  // Fetch job descriptions for filtering and upload
  const fetchJobs = async () => {
    try {
      const jobData = await jobDescriptionService.getAll();
      console.log('Fetched job data:', jobData);

      //  : Handle the correct response structure from getAll()
      let jobList: any[] = [];
      if (jobData.jobs) {
        jobList = jobData.jobs;
      } else if (Array.isArray(jobData)) {
        jobList = jobData;
      }

      const normalizedJobs = jobList.map((job: any) => ({
        id: job.jobId || job.id,
        title: job.jobTitle || job.title,
        status: job.status || 'Active',
      }));

      setJobs(normalizedJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    }
  };

  const [panels, setPanels] = useState<any[]>([]);
  const [loadingPanels, setLoadingPanels] = useState(false);
  const [panelsError, setPanelsError] = useState<string | null>(null);

  // Add function to load interview panels
  const loadInterviewPanels = async () => {
    setLoadingPanels(true);
    setPanelsError(null);

    try {
      console.log('Loading interview panelists from backend...');

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${API_ENDPOINTS.USERS.LIST}?page=1&size=100&role=panelist`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch panelists: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Panelists response from backend:', data);

      if (data.users && Array.isArray(data.users) && data.users.length > 0) {
        const transformedPanels = data.users
          .map((user: any) => ({
            id: user.username || user.id,
            name:
              user.full_name ||
              user.name ||
              `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
              user.username,
            email: user.email,
            role: user.role || 'Panelist',
            username: user.username,
            isActive: user.isActive !== false,
          }))
          .filter(panel => panel.isActive);

        setPanels(transformedPanels);
        console.log('Loaded panelists from backend:', transformedPanels);

        if (transformedPanels.length === 0) {
          setPanelsError('No active panelists found in the system.');
        }
      } else {
        setPanels([]);
        setPanelsError(
          'No panelists found. Please contact administrator to add panelists to the system.'
        );
      }
    } catch (error) {
      console.error('Error loading interview panelists:', error);
      setPanels([]); // Set empty array - NO fallback data
      setPanelsError(
        error instanceof Error
          ? `Failed to load panelists: ${error.message}`
          : 'Failed to load interview panelists. Please check your connection and try again.'
      );
    } finally {
      setLoadingPanels(false);
    }
  };

  // handler for the interview scheduling
  const handleInterviewScheduling = async (
    resumeId: string,
    panelId: string,
    scheduleData: any
  ) => {
    try {
      // Assign panel and schedule interview
      await resumeService.assignPanelToResumeWithStatus(resumeId, panelId, {
        interviewDateTime: scheduleData.interviewDateTime || null,
        duration: scheduleData.duration || 60,
        notes: scheduleData.notes || '',
        avatureFeedbackLink: scheduleData.avatureFeedbackLink || '',
        candidateName: scheduleData.candidateName,
        candidateEmail: scheduleData.candidateEmail,
        jobId: scheduleData.jobId,
        jobTitle: scheduleData.jobTitle,
        panelistName: scheduleData.panelistName,
        panelistEmail: scheduleData.panelistEmail,
      });
      setSnackbar({
        open: true,
        message: 'Panel assigned and interview scheduled successfully.',
        severity: 'success',
      });

      // Refresh data
      const type = currentTab === 0 ? 'jd' : 'event';
      fetchResumes(currentPage, pageSize, type);
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to assign panel.',
        severity: 'error',
      });
    }
  };

  // Handle resume validation with dimensions
  const handleValidateResumes = async (resumeIds: string[], dimensions: string[]) => {
    if (resumeIds.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select resumes to validate.',
        severity: 'warning',
      });
      return;
    }

    setValidating(true);
    try {
      let validationResults;
      try {
        // Pass dimensions to the validation service
        validationResults = await resumeService.validateResumes(resumeIds, dimensions);
      } catch (error) {
        console.warn('Resume validation service not available, using mock validation');
        // Mock validation with dimension-based scoring
        validationResults = resumeIds.map(resumeId => ({
          resumeId,
          score: Math.floor(Math.random() * 40) + 60,
          dimensions: dimensions.reduce(
            (acc, dim) => {
              acc[dim] = Math.floor(Math.random() * 40) + 60;
              return acc;
            },
            {} as Record<string, number>
          ),
        }));
      }

      setResumes(prevResumes =>
        prevResumes.map(resume => {
          const result = validationResults.find((r: any) => r.resumeId === resume.id);
          return result ? { ...resume, score: result.score } : resume;
        })
      );

      setSnackbar({
        open: true,
        message: `Successfully validated ${resumeIds.length} resume(s) across ${dimensions.length} dimension(s).`,
        severity: 'success',
      });

      setSelectedResumes([]);
      setValidationDialogOpen(false);
    } catch (error) {
      console.error('Error validating resumes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to validate resumes. Please try again.',
        severity: 'error',
      });
    } finally {
      setValidating(false);
    }
  };

  //  : Handle resume upload with refresh
  const handleUploadResumes = async (jobId: string, files: File[], additionalData?: any) => {
    setLoading(true);
    try {
      // Extract type and job summary from additional data
      const uploadType = additionalData?.uploadMode === 'event' ? 'event' : 'jd';
      const jobSummary = additionalData?.jobSummary || '';
      
      // Prepare additional data with type
      const uploadData = {
        ...additionalData,
        type: uploadType
      };
      
      const result = await resumeService.uploadResumes(jobId, files, jobSummary, '', uploadData);

      console.log('Upload result:', result);

      let message = '';
      if (result.total_uploaded && result.total_failed) {
        message = `Upload completed: ${result.total_uploaded} successful, ${result.total_failed} failed.`;
      } else if (result.total_uploaded) {
        message = `Successfully uploaded ${result.total_uploaded} resume(s).`;
      } else {
        message = result.message || 'Upload completed.';
      }

      if (result.failed_files && result.failed_files.length > 0) {
        const failedList = result.failed_files
          .map((f: any) => `• ${f.filename}: ${f.error}`)
          .join('\n');
        message += `\n\nFailed uploads:\n${failedList}`;
      }

      setSnackbar({
        open: true,
        message: message,
        severity: result.total_failed && result.total_failed > 0 ? 'warning' : 'success',
      });

      setUploadDialogOpen(false);

      //  : Refresh with current pagination
      setTimeout(async () => {
        const type = currentTab === 0 ? 'jd' : 'event';
        await fetchResumes(currentPage, pageSize, type);
      }, 100);
    } catch (error) {
      console.error('Error uploading resumes:', error);

      let errorMessage = 'Failed to upload resumes. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk delete (HR/Admin only)
  const handleBulkDelete = async () => {
    if (selectedResumes.length === 0) return;

    try {
      console.log("bulk delete service doesn't exist");
      setSnackbar({
        open: true,
        message: `Successfully deleted ${selectedResumes.length} resume(s).`,
        severity: 'success',
      });

      setSelectedResumes([]);
      //  : Refresh with current pagination
      const type = currentTab === 0 ? 'jd' : 'event';
      fetchResumes(currentPage, pageSize, type);
    } catch (error) {
      console.error('Error deleting resumes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete resumes. Please try again.',
        severity: 'error',
      });
    }
  };

  // Handle individual resume status update (SME functionality)
  const handleStatusUpdate = async (resumeId: string, newStatus: string) => {
    try {
      // Use the new API method
      const result = await resumeService.updateResumeStatus(
        resumeId,
        newStatus,
        `Status updated to ${newStatus}`,
        ''
      );

      console.log('Status update result:', result);

      // Update local state
      setResumes(prevResumes =>
        prevResumes.map(resume =>
          resume.id === resumeId ? { ...resume, status: newStatus } : resume
        )
      );

      setSnackbar({
        open: true,
        message: `Resume status updated to "${RESUME_STATUS_OPTIONS.find(opt => opt.value === newStatus)?.label || newStatus}" successfully.`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating resume status:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update resume status.',
        severity: 'error',
      });
    }
  };

  // Handle individual resume delete
  const handleDeleteResume = async (resumeId: string) => {
    try {
      await resumeService.deleteResume(resumeId);

      setResumes(prevResumes => prevResumes.filter(resume => resume.id !== resumeId));

      setSnackbar({
        open: true,
        message: 'Resume deleted successfully.',
        severity: 'success',
      });

      //  : Refresh to maintain pagination consistency
      setTimeout(() => {
        const type = currentTab === 0 ? 'jd' : 'event';
        fetchResumes(currentPage, pageSize, type);
      }, 100);
    } catch (error) {
      console.error('Error deleting resume:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete resume.',
        severity: 'error',
      });
    }
  };

  // Handle view/update resume
  const handleViewResume = (resume: Resume) => {
    setSelectedResume(resume);
    setViewDialogOpen(true);
  };

  // Handle update resume
  const handleUpdateResume = async (resumeId: string, updates: any) => {
    try {
      await resumeService.updateResumeDetails(resumeId, updates);

      setResumes(prevResumes =>
        prevResumes.map(resume => (resume.id === resumeId ? { ...resume, ...updates } : resume))
      );

      setSnackbar({
        open: true,
        message: 'Resume updated successfully.',
        severity: 'success',
      });

      //  : Refresh with current pagination
      const type = currentTab === 0 ? 'jd' : 'event';
      await fetchResumes(currentPage, pageSize, type);
    } catch (error) {
      console.error('Error updating resume:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update resume.',
        severity: 'error',
      });
    }
  };

  // Handle download resume
  const handleDownload = async (resumeId: string, fileName?: string) => {
    try {
      setLoading(true);

      await resumeService.downloadResume(resumeId, fileName);

      setSnackbar({
        open: true,
        message: 'Resume downloaded successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error downloading resume:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to download resume',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedResumes(filteredResumes.map(resume => resume.id));
    } else {
      setSelectedResumes([]);
    }
  };

  const handleSelectResume = (resumeId: string, checked: boolean) => {
    if (checked) {
      setSelectedResumes(prev => [...prev, resumeId]);
    } else {
      setSelectedResumes(prev => prev.filter(id => id !== resumeId));
    }
  };

  const filteredResumes = getFilteredResumes();

  console.log('Filtered resumes for display:', filteredResumes.length);
  console.log('Sample filtered resume:', filteredResumes[0]);

  //   Filter resumes based on search and filters
  // const filteredResumes = resumes.filter(resume => {
  //   const matchesSearch =
  //     !searchTerm ||
  //     resume.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     (resume.email && resume.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
  //     resume.jobId.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     (resume.jobTitle && resume.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()));

  //   //   Job filter - check both job ID and job title
  //   const matchesJobFilter =
  //     !filterJobId ||
  //     resume.jobId === filterJobId ||
  //     resume.jobId.toLowerCase().includes(filterJobId.toLowerCase()) ||
  //     (resume.jobTitle && resume.jobTitle.toLowerCase().includes(filterJobId.toLowerCase()));

  //   const matchesStatusFilter = !filterStatus || resume.status === filterStatus;

  //   // ADD score filter logic
  //   const matchesScoreFilter =
  //     !filterScore ||
  //     (() => {
  //       const score = resume.score;

  //       if (filterScore === 'not_validated') {
  //         return !score || score === 0;
  //       }

  //       if (!score || score === 0) {
  //         return false; // If no score and looking for specific ranges, exclude
  //       }

  //       switch (filterScore) {
  //         case '90+':
  //           return score >= 90;
  //         case '80-89':
  //           return score >= 80 && score < 90;
  //         case '70-79':
  //           return score >= 70 && score < 80;
  //         case '60-69':
  //           return score >= 60 && score < 70;
  //         case '50-59':
  //           return score >= 50 && score < 60;
  //         case '<50':
  //           return score < 50;
  //         default:
  //           return true;
  //       }
  //     })();

  //   return matchesSearch && matchesJobFilter && matchesStatusFilter && matchesScoreFilter;
  // });

  const isAllSelected =
    selectedResumes.length === filteredResumes.length && filteredResumes.length > 0;
  const isSomeSelected =
    selectedResumes.length > 0 && selectedResumes.length < filteredResumes.length;

  const getScoreFilterOptions = () => [
    { value: '', label: 'All Scores' },
    { value: 'not_validated', label: 'Not Validated' },
    { value: '90+', label: '90% and above' },
    { value: '80-89', label: '80% - 89%' },
    { value: '70-79', label: '70% - 79%' },
    { value: '60-69', label: '60% - 69%' },
    { value: '50-59', label: '50% - 59%' },
    { value: '<50', label: 'Below 50%' },
  ];

  const getUniqueStatuses = () => {
    const statuses = resumes.map(resume => resume.status).filter(Boolean);
    const uniqueStatuses = Array.from(new Set(statuses));
    return [
      { value: '', label: 'All Statuses' },
      ...uniqueStatuses.map(status => ({
        value: status,
        label: status || 'No Status',
        color: getStatusColor(status),
      })),
    ];
  };

  //   Update getStatusColor to handle any status value
  const getStatusColor = (status: string) => {
    if (!status || status === 'N/A' || status === '') return COLORS.GRAY_500;

    // Map common status patterns to colors
    const statusLower = status.toLowerCase();

    if (statusLower.includes('submit')) return '#5A6A85';
    if (statusLower.includes('review')) return '#f59e0b';
    if (statusLower.includes('interview') || statusLower.includes('scheduled')) return '#24936E';
    if (statusLower.includes('reject')) return '#ef4444';
    if (statusLower.includes('hire') || statusLower.includes('selected')) return '#10b981';
    if (statusLower.includes('withdraw')) return '#6b7280';
    if (statusLower.includes('shortlist')) return '#06b6d4';
    if (statusLower.includes('move') || statusLower.includes('next')) return '#f97316';

    // Default color for unknown statuses
    return '#64748b';
  };

  // Don't render the main content until auth is loaded
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: COLORS.GRAY_100, py: 3, px: 2 }}>
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" className="resumes-title">
            {userRole === 'sme' ? 'My Assigned Resumes' : 'Resumes'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {userRole === 'sme'
              ? 'Review and manage resumes assigned to you for evaluation.'
              : 'Manage all resumes, upload new ones, and validate candidate profiles.'}
          </Typography>
        </Box>

        {/* Event/JD Navigation Tabs */}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                  {stats.totalResumes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userRole === 'sme' ? 'Assigned Resumes' : 'Total Resumes'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.SUCCESS_DARK, fontWeight: 600 }}>
                  {stats.underReview}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Under Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                  {stats.shortlisted}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Shortlisted
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.SUCCESS_DARK, fontWeight: 600 }}>
                  {stats.selected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selected/Active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.ERROR_DARK, fontWeight: 600 }}>
                  {stats.rejected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rejected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Compact Search and Filter Bar */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, or job ID..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1.2rem', color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Job"
                value={filterJobId}
                onChange={e => setFilterJobId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                <MenuItem value="">All Jobs</MenuItem>
                {(() => {
                  const jobsFromResumes = resumes.map(resume => ({
                    id: resume.jobId,
                    title: resume.jobTitle || 'Unknown Job',
                  }));

                  const allJobs = [...jobs, ...jobsFromResumes];
                  const uniqueJobs = allJobs.reduce((acc: any[], job) => {
                    if (!acc.find(existingJob => existingJob.id === job.id)) {
                      acc.push(job);
                    }
                    return acc;
                  }, []);

                  // Sort by job ID
                  uniqueJobs.sort((a, b) => (a.id || '').localeCompare(b.id || ''));

                  return uniqueJobs.map(job => (
                    <MenuItem key={job.id} value={job.id}>
                      <Box>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {job.id} - {job.title}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ));
                })()}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                {getUniqueStatuses().map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* ADD Score Filter */}
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Score"
                value={filterScore}
                onChange={e => setFilterScore(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    backgroundColor: '#f8fafc',
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                {getScoreFilterOptions().map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{option.label}</Typography>
                      {option.value === 'not_validated' && (
                        <Chip
                          label="No Score"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.65rem',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                          }}
                        />
                      )}
                      {option.value.includes('+') && (
                        <Chip
                          label="Excellent"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.65rem',
                            backgroundColor: '#dcfce7',
                            color: '#15803d',
                          }}
                        />
                      )}
                      {option.value.includes('80-89') && (
                        <Chip
                          label="Good"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.65rem',
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                          }}
                        />
                      )}
                      {option.value.includes('<50') && (
                        <Chip
                          label="Poor"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.65rem',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {(userRole === 'hr' || userRole === 'admin') && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<UploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      minWidth: 'auto',
                      backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                        filter: 'brightness(0.9)',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    Upload Resumes
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Compact Action Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            px: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={validating ? <CircularProgress size={16} /> : <ValidateIcon />}
              onClick={() => setValidationDialogOpen(true)}
              disabled={selectedResumes.length === 0 || validating}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                borderColor: 'var(--primary-blue)',
                color: 'var(--primary-blue)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  borderColor: 'var(--primary-blue)',
                },
              }}
            >
              {validating ? 'Validating...' : `Validate (${selectedResumes.length})`}
            </Button>
            {(userRole === 'hr' || userRole === 'admin') && selectedResumes.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<BulkDeleteIcon />}
                onClick={handleBulkDelete}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  },
                }}
              >
                Delete ({selectedResumes.length})
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/*  : Clear filters to include score filter */}
            {(searchTerm || filterJobId || filterStatus || filterScore) && (
              <Button
                size="small"
                onClick={() => {
                  setSearchTerm('');
                  setFilterJobId('');
                  setFilterStatus('');
                  setFilterScore(''); // ADD clear score filter
                }}
                sx={{
                  textTransform: 'none',
                  color: '#64748b',
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                  },
                }}
              >
                Clear filters
              </Button>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Showing {filteredResumes.length} of {totalResumes} resumes
            </Typography>
            {/* ADD filter status indicator */}
            {(searchTerm || filterJobId || filterStatus || filterScore) && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {searchTerm && (
                  <Chip
                    label={`Search: "${searchTerm}"`}
                    size="small"
                    onDelete={() => setSearchTerm('')}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {filterJobId && (
                  <Chip
                    label={`Job: ${filterJobId}`}
                    size="small"
                    onDelete={() => setFilterJobId('')}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {filterStatus && (
                  <Chip
                    label={`Status: ${filterStatus}`}
                    size="small"
                    onDelete={() => setFilterStatus('')}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {filterScore && (
                  <Chip
                    label={`Score: ${getScoreFilterOptions().find(opt => opt.value === filterScore)?.label}`}
                    size="small"
                    onDelete={() => setFilterScore('')}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: '1px solid #e0e0e0',
              '& .MuiTabs-indicator': {
                backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                height: 3,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#64748b',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  color: COLORS.PRIMARY_BLUE_DARK,
                },
                '&:hover': {
                  color: COLORS.PRIMARY_BLUE_DARK,
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              },
            }}
          >
            <Tab
              icon={<WorkOutline />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Job Descriptions</span>
                  <Chip
                    label={tabCounts.jd}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      backgroundColor: currentTab === 0 ? COLORS.PRIMARY_BLUE_DARK : '#e2e8f0',
                      color: currentTab === 0 ? 'white' : '#64748b',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              }
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<EventNote />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Events</span>
                  <Chip
                    label={tabCounts.event}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      backgroundColor: currentTab === 1 ? COLORS.PRIMARY_BLUE_DARK : '#e2e8f0',
                      color: currentTab === 1 ? 'white' : '#64748b',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              }
              sx={{ minHeight: 64 }}
            />
          </Tabs>
        </Paper>
        {/* Resumes Table */}
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isSomeSelected && !isAllSelected}
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Candidate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Job ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Job Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Experience</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                  {/* Add Assign Panel column for HR/Admin users */}
                  {(userRole === 'hr' || userRole === 'admin') && (
                    <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                      Assign/Reassign Panel
                    </TableCell>
                  )}
                  {userRole === 'sme' && (
                    <TableCell sx={{ fontWeight: 600 }}>Update Status</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoadingResumes ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        userRole === 'hr' || userRole === 'admin'
                          ? 12
                          : userRole === 'sme'
                            ? 11
                            : 10
                      }
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading resumes...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredResumes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        userRole === 'hr' || userRole === 'admin'
                          ? 12
                          : userRole === 'sme'
                            ? 11
                            : 10
                      }
                      sx={{ textAlign: 'center', py: 4 }}
                    >
                      <Typography color="text.secondary">
                        {resumes.length === 0
                          ? userRole === 'sme'
                            ? 'No resumes assigned to you yet.'
                            : 'No resumes uploaded yet. Click "Upload" to add resumes.'
                          : 'No resumes match your search criteria.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredResumes.map(resume => (
                    <TableRow key={resume.id} hover selected={selectedResumes.includes(resume.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedResumes.includes(resume.id)}
                          onChange={e => handleSelectResume(resume.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {resume.candidateName}
                          </Typography>
                          {resume.email && (
                            <Typography variant="caption" color="text.secondary">
                              {resume.email}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: 'var(--primary-blue)', fontWeight: 500 }}
                        >
                          {resume.jobId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{resume.jobTitle}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={resume.status || 'No Status'}
                          size="small"
                          sx={{
                            backgroundColor: `${getStatusColor(resume.status)}20`,
                            color: getStatusColor(resume.status),
                            fontWeight: 600,
                            borderRadius: '12px',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {resume.score && resume.score > 0 ? (
                          <Chip
                            label={`${resume.score}%`}
                            size="small"
                            sx={{
                              backgroundColor:
                                resume.score >= 80
                                  ? COLORS.SUCCESS_LIGHT
                                  : resume.score >= 60
                                    ? COLORS.WARNING_LIGHT
                                    : COLORS.ERROR_LIGHT,
                              color:
                                resume.score >= 80
                                  ? COLORS.SUCCESS_DARK
                                  : resume.score >= 60
                                    ? COLORS.SUCCESS_DARK
                                    : COLORS.ERROR_DARK,
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not validated
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {(resume as any)._original?.experience_years || 0} years
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {(resume as any)._original?.location || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {resume.uploadedDate
                            ? new Date(resume.uploadedDate).toLocaleDateString()
                            : 'N/A'}
                        </Typography>
                      </TableCell>

                      {/* Enhanced Assign Panel cell for HR/Admin users */}
                      {(userRole === 'hr' || userRole === 'admin') && (
                        <TableCell sx={{ minWidth: 250 }}>
                          {resume.assignedPanel ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {/* Current Panel Assignment Card */}
                              <Card
                                sx={{
                                  backgroundColor: '#f0f9ff',
                                  border: '1px solid #bae6fd',
                                  borderRadius: 2,
                                  boxShadow: 'none',
                                }}
                              >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        backgroundColor: COLORS.PRIMARY_BLUE_DARK,
                                        fontSize: '0.75rem',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {(() => {
                                        const foundPanel = panels.find(
                                          p => p.id === resume.assignedPanel
                                        );
                                        const displayName =
                                          foundPanel?.name || resume.assignedPanel;
                                        return displayName.charAt(0).toUpperCase();
                                      })()}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          color: '#1f2937',
                                          fontSize: '0.8rem',
                                          lineHeight: 1.2,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {/*  : Show panel name or backend value directly */}
                                        {(() => {
                                          const foundPanel = panels.find(
                                            p => p.id === resume.assignedPanel
                                          );
                                          return foundPanel?.name || resume.assignedPanel;
                                        })()}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: '0.65rem',
                                          lineHeight: 1,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          display: 'block',
                                        }}
                                      >
                                        {/*  : Show email only if panel is found, otherwise show role/type */}
                                        {(() => {
                                          const foundPanel = panels.find(
                                            p => p.id === resume.assignedPanel
                                          );
                                          return foundPanel?.email || 'Panelist';
                                        })()}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Status indicator */}
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      label={
                                        resume.status === 'interview_scheduled'
                                          ? 'Scheduled'
                                          : 'Assigned'
                                      }
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 500,
                                        backgroundColor:
                                          resume.status === 'interview_scheduled'
                                            ? '#dcfce7'
                                            : '#fef3c7',
                                        color:
                                          resume.status === 'interview_scheduled'
                                            ? '#24936E'
                                            : '#d97706',
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  </Box>
                                </CardContent>
                              </Card>

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    setSelectedResumeForInterview(resume);
                                    setInterviewModalOpen(true);
                                  }}
                                  disabled={loadingPanels}
                                  sx={{
                                    flex: 1,
                                    height: 28,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    borderColor: '#d1d5db',
                                    color: '#6b7280',
                                    '&:hover': {
                                      backgroundColor: '#f9fafb',
                                      borderColor: '#9ca3af',
                                      color: '#374151',
                                    },
                                    '&:disabled': {
                                      borderColor: '#e5e7eb',
                                      color: '#9ca3af',
                                    },
                                  }}
                                >
                                  {loadingPanels ? 'Loading...' : 'Reassign'}
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            // Show "Assign Panel" button for initial assignment
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AssignIcon />}
                                onClick={() => {
                                  setSelectedResumeForInterview(resume);
                                  setInterviewModalOpen(true);
                                }}
                                disabled={loadingPanels}
                                sx={{
                                  minHeight: 36,
                                  borderRadius: 2,
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  borderColor: 'var(--primary-blue)',
                                  color: 'var(--primary-blue)',
                                  backgroundColor: 'rgba(59, 130, 246, 0.02)',
                                  border: '2px dashed var(--primary-blue)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                    borderColor: 'var(--primary-blue)',
                                    borderStyle: 'solid',
                                  },
                                  '&:disabled': {
                                    borderColor: '#e5e7eb',
                                    color: '#9ca3af',
                                    backgroundColor: '#f9fafb',
                                  },
                                }}
                              >
                                {loadingPanels ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    Loading...
                                  </Box>
                                ) : (
                                  'Assign Panel'
                                )}
                              </Button>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  fontSize: '0.65rem',
                                  fontStyle: 'italic',
                                }}
                              >
                                No panel assigned
                              </Typography>
                            </Box>
                          )}

                          {panelsError && (
                            <Alert
                              severity="error"
                              sx={{
                                mt: 1,
                                fontSize: '0.65rem',
                                '& .MuiAlert-message': { fontSize: '0.65rem' },
                                '& .MuiAlert-icon': { fontSize: '0.9rem' },
                              }}
                            >
                              {panelsError}
                            </Alert>
                          )}
                        </TableCell>
                      )}

                      {userRole === 'sme' && (
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={resume.status}
                            onChange={e => handleStatusUpdate(resume.id, e.target.value)}
                            sx={{ minWidth: 120 }}
                          >
                            {/* Use dynamic status options but exclude empty option for updates */}
                            {getUniqueStatuses()
                              .slice(1)
                              .map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            {/* Add some common status options if they're not in the current data */}
                            {!getUniqueStatuses().find(s => s.value === 'submitted') && (
                              <MenuItem value="submitted">Submitted</MenuItem>
                            )}
                            {!getUniqueStatuses().find(s => s.value === 'reviewing') && (
                              <MenuItem value="reviewing">Reviewing</MenuItem>
                            )}
                            {!getUniqueStatuses().find(s => s.value === 'shortlisted') && (
                              <MenuItem value="shortlisted">Shortlisted</MenuItem>
                            )}
                            {!getUniqueStatuses().find(s => s.value === 'rejected') && (
                              <MenuItem value="rejected">Rejected</MenuItem>
                            )}
                            {!getUniqueStatuses().find(s => s.value === 'interview_scheduled') && (
                              <MenuItem value="interview_scheduled">Interview Scheduled</MenuItem>
                            )}
                          </TextField>
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View & Update Resume">
                            <IconButton
                              size="small"
                              onClick={() => handleViewResume(resume)}
                              sx={{
                                color: '#64748b',
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 1,
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#f1f5f9',
                                  borderColor: '#cbd5e1',
                                  color: '#475569',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Resume">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(resume.id, resume.fileName)}
                              sx={{
                                color: '#64748b',
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 1,
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#ecfdf5',
                                  borderColor: '#bbf7d0',
                                  color: '#059669',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(userRole === 'hr' || userRole === 'admin') && (
                            <Tooltip title="Delete Resume">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteResume(resume.id)}
                                sx={{
                                  color: '#64748b',
                                  backgroundColor: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 1,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    backgroundColor: '#fef2f2',
                                    borderColor: '#fecaca',
                                    color: '#dc2626',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        {/*  : Pagination Controls */}
        {totalResumes > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3,
              mb: 2,
              px: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {(() => {
                  const hasActiveFilters = searchTerm || filterJobId || filterStatus || filterScore;

                  if (hasActiveFilters) {
                    return `Showing ${filteredResumes.length} of ${totalResumes} resumes (filtered)`;
                  } else {
                    const startIndex = (currentPage - 1) * pageSize + 1;
                    const endIndex = Math.min(currentPage * pageSize, totalResumes);
                    return `Showing ${startIndex} to ${endIndex} of ${totalResumes} resumes`;
                  }
                })()}
              </Typography>
            </Box>

            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => handlePageChange(page)}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              disabled={isLoadingResumes}
            />
          </Box>
        )}

        {/* Upload Resume Dialog */}
        {(userRole === 'hr' || userRole === 'admin') && (
          <UploadResumeDialog
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            onUpload={handleUploadResumes}
            jobs={jobs}
            loading={loading}
          />
        )}

        {/* Resume View & Update Dialog */}
        <ResumeViewUpdateDialog
          open={viewDialogOpen}
          resume={selectedResume}
          onClose={() => {
            setViewDialogOpen(false);
            setSelectedResume(null);
          }}
          onUpdate={handleUpdateResume}
          onDownload={handleDownload}
          userRole={userRole}
          loading={loading}
        />

        {/* Interview Scheduling Modal */}
        <InterviewSchedulingModal
          open={interviewModalOpen}
          onClose={() => {
            setInterviewModalOpen(false);
            setSelectedResumeForInterview(null);
          }}
          resume={selectedResumeForInterview}
          panels={panels.map(p => ({
            id: p.id,
            name: p.name,
            email: p.email || '',
            role: p.role || 'Panelist',
          }))}
          onSchedule={handleInterviewScheduling}
          loading={loading}
        />

        {/* Resume Validation Dialog */}
        <ResumeValidationDialog
          open={validationDialogOpen}
          onClose={() => setValidationDialogOpen(false)}
          selectedResumes={resumes.filter(r => selectedResumes.includes(r.id))}
          onValidate={handleValidateResumes}
          loading={validating}
        />

        {/* Snackbar for notifications */}
        <SnackbarAlert snackbar={snackbar} setSnackbar={setSnackbar} />
      </Box>
    </Box>
  );
};

export default ResumesPage;
